import { z } from 'zod';

import { Api } from '@automapa/svelte-automapa-template';
import type {
    CountType,
    VehicleLastPositionResponseType,
    VehicleLastPositionType,
    VehicleRequestType,
    VehicleResponseType,
} from './types';
import {
    CountSchema,
    VehicleLastPositionResponseSchema,
    VehicleLastPositionSchema,
    VehicleRequestSchema,
    VehicleResponseSchema,
} from './types';
import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
import { type QueriesType, QueriesTypeSchema } from '../telematics/types';

export const VehicleColorsResponseSchema = z.record(z.string().nullable()).catch({});
export type VehicleColorsResponseType = z.infer<typeof VehicleColorsResponseSchema>;

export const VehicleGroupResponseSchema = z.object({
    group_id: z.number(),
    // customer_id: z.number(),
    group_name: z.string(),
    vehicle_ids: z.array(z.number()),
});
export type VehicleGroupResponseType = z.infer<typeof VehicleGroupResponseSchema>;

export class VehiclesApi {
    public static async getAll(
        api: Api,
        is_group: boolean | null = false
    ): Promise<VehicleResponseType[]> {
        let filter = new ApiFilterBuilder(null);
        filter.sortBy('name', 'asc');
        if (is_group !== null) filter.addFilterByBoolean('is_group', is_group);
        return api.getData<undefined, VehicleResponseType[]>(
            'get',
            '/vehicles' + filter.getUri(),
            undefined,
            z.undefined(),
            z.array(VehicleResponseSchema)
        );
    }

    public static async colorize(api: Api): Promise<number[]> {
        return api.getData<undefined, number[]>(
            'get',
            '/vehicles/colorize',
            undefined,
            z.undefined(),
            z.array(z.number())
        );
    }

    public static async updateOne(
        api: Api,
        vehicleId: number,
        vehicle: VehicleRequestType
    ): Promise<VehicleResponseType> {
        return api.getData<VehicleRequestType, VehicleResponseType>(
            'put',
            `/vehicle/${vehicleId}`,
            vehicle,
            VehicleRequestSchema,
            VehicleResponseSchema
        );
    }

    public static async createOne(
        api: Api,
        vehicle: VehicleRequestType
    ): Promise<VehicleResponseType> {
        return api.getData<VehicleRequestType, VehicleResponseType>(
            'post',
            `/vehicle`,
            vehicle,
            VehicleRequestSchema,
            VehicleResponseSchema
        );
    }

    public static async removeOne(api: Api, vehicleId: number): Promise<any> {
        return api.delete(`/vehicle/${vehicleId}`);
    }

    public static async removeAll(api: Api): Promise<any> {
        let vehicles = await VehiclesApi.getAll(api);
        // let promises: VehicleResponseType[] = [];
        let promises: any[] = [];
        vehicles.forEach(v => {
            if (v.id) promises.push(VehiclesApi.removeOne(api, v.id));
        });
        return Promise.all(promises);
    }

    public static async getOne(api: Api, vehicleId: number): Promise<VehicleResponseType> {
        return api.getData<undefined, VehicleResponseType>(
            'get',
            `/vehicle/${vehicleId}`,
            undefined,
            z.undefined(),
            VehicleResponseSchema
        );
    }

    public static async getVehicleGroups(api: Api): Promise<any[]> {
        return api.getData<{}, any[]>(
            'get',
            `/vehicle_group_membership/`,
            {},
            z.object({}),
            z.any()
        );
    }

    public static async getVehicleGroupItems(api: Api): Promise<VehicleGroupResponseType[]> {
        return api.getData<{}, VehicleGroupResponseType[]>(
            'get',
            `/vehicle_group`,
            {},
            z.object({}),
            z.array(VehicleGroupResponseSchema)
        );
    }

    public static async getCount(
        api: Api,
        filter: ApiFilterBuilder,
        is_group: boolean = false
    ): Promise<CountType> {
        filter.addFilterByBoolean('is_group', is_group);
        return api.getData<undefined, CountType>(
            'get',
            '/vehicles/count' + filter.getUri(),
            undefined,
            z.undefined(),
            CountSchema
        );
    }

    public static async filter(
        api: Api,
        filter: ApiFilterBuilder,
        is_group: boolean = false
    ): Promise<VehicleResponseType[]> {
        filter.sortBy('name', 'asc');
        filter.addFilterByBoolean('is_group', is_group);
        return api.getData<undefined, VehicleResponseType[]>(
            'get',
            '/vehicles' + filter.getUri(),
            undefined,
            z.undefined(),
            z.array(VehicleResponseSchema)
        );
    }

    public static async getWithQueries(
        api: Api,
        queries: QueriesType,
        filter: ApiFilterBuilder,
        is_group: boolean = false
    ): Promise<VehicleResponseType[]> {
        filter.sortBy('name', 'asc');
        filter.addFilterByBoolean('is_group', is_group);
        return api.getData<QueriesType, VehicleResponseType[]>(
            'post',
            '/vehicle/query' + filter.getUri(),
            queries,
            QueriesTypeSchema,
            z.array(VehicleResponseSchema)
        );
    }

    public static async verifyEmail(
        api: Api,
        email: string
    ): Promise<{ valid: boolean; reason: string }> {
        return api.getData<{ email: string }, { valid: boolean; reason: string }>(
            'post',
            '/vehicles/verify_email',
            { email },
            z.object({ email: z.string() }),
            z.object({ valid: z.boolean(), reason: z.string() })
        );
    }

    public static async patch(api: Api, vehicleId: number, path: string, value: any): Promise<any> {
        return api.patch<VehicleResponseType>(
            `/vehicle/${vehicleId}`,
            'replace',
            `/${path}`,
            value,
            VehicleResponseSchema
        );
    }

    // {"14":null,"15":null,"16":null,"17":null,"19":"#e8e8a0","20":"#fae06e","23":null,"24":null,"25":null,"26":null,"18":"#c7f587","21":"#ffa87d","22":"#ff7e5e","27":"#ffa348","28":null,"29":null,"30":null,"31":"#c7f587","32":"#e8e8a0","33":"#fae06e","34":"#ffa87d","35":"#ff7e5e","36":null,"37":null,"38":null,"39":null}
    public static async getColors(api: Api): Promise<VehicleColorsResponseType> {
        return api.getData<any, VehicleColorsResponseType>(
            'post',
            '/vehicles/colors',
            {},
            z.object({}),
            VehicleColorsResponseSchema
        );
    }

    // public static async getLastPositions(api: Api, vehicleIds: number[]): Promise<VehicleLastPositionType[]> {
    //     return api.getData<any, VehicleLastPositionType[]>(
    //         'post',
    //         '/vehicles/last_position',
    //         {vehicle_ids: vehicleIds},
    //         z.any(),
    //         z.array(VehicleLastPositionSchema)
    //     );
    // }

    // public static async getLastPositions(api: Api, vehicleIds: number[], random: boolean = false): Promise<VehicleLastPositionResponseType> {
    //     return api.getData<any, VehicleLastPositionResponseType>(
    //         'post',
    //         '/vehicles/last_position',
    //         {vehicle_ids: vehicleIds, random},
    //         z.any(),
    //         VehicleLastPositionResponseSchema
    //     );
    // }

    public static async getLastPositions(
        api: Api,
        ids: number[],
        random: boolean = false,
        time_compression_factor: number | null = null
    ): Promise<VehicleLastPositionResponseType> {
        return api.getData<any, VehicleLastPositionResponseType>(
            'post',
            '/vehicles/get_last_position_by_assignment',
            { id: ids, random, time_compression_factor: time_compression_factor ?? null },
            z.any(),
            VehicleLastPositionResponseSchema
        );
    }

    public static async setVehicleGroups(
        api: Api,
        vehicleId: number,
        groupIds: number[]
    ): Promise<any> {
        return api.getData<any, any>(
            'post',
            `/vehicle_group/set_groups`,
            {
                vehicle_id: vehicleId,
                group_ids: groupIds,
            },
            z.any(),
            z.any()
        );
    }

    public static async setVehiclesInGroup(
        api: Api,
        groupId: number,
        vehicleIds: number[]
    ): Promise<any> {
        return api.getData<any, any>(
            'post',
            `/vehicle_group/set_vehicles_in_group`,
            {
                group_id: groupId,
                vehicle_ids: vehicleIds,
            },
            z.any(),
            z.any()
        );
    }

    public static async getUniqueVehicleIds(
        api: Api,
        vehicle_or_group_ids: number[]
    ): Promise<number[]> {
        return api.getData<any, number[]>(
            'post',
            `/vehicle_group/get_unique_vehicle_ids`,
            { vehicle_or_group_ids },
            z.any(),
            z.array(z.number())
        );
    }
}
